/**
 * Site-wide SEO constants and helpers.
 *
 * Everything crawler-facing (canonicals, sitemap, robots, JSON-LD) resolves
 * through here so there is one place that knows the public origin.
 *
 * NOTE: always the WWW origin. Vercel 307-redirects the apex to www at the
 * domain level, so an apex canonical would point crawlers at a redirect.
 */
export const SITE_URL = (
  process.env.NEXT_PUBLIC_SITE_URL ?? 'https://www.yespersonalized.com'
).replace(/\/$/, '')

export const SITE_NAME = 'YES! Personalized'
export const LEGAL_NAME = 'allthe ltd'

/**
 * Shopify App Store listing — every "Install" CTA on the site points here.
 *
 * The literal is the real source of truth, not a placeholder: several of the
 * call sites (the marketing chrome, the About page) end up in client bundles,
 * where a non-`NEXT_PUBLIC_` env var is replaced with `undefined` at build
 * time and the fallback is what actually ships. The env vars are kept as an
 * override for staging/unlisted builds.
 */
export const INSTALL_URL =
  process.env.NEXT_PUBLIC_SHOPIFY_APP_INSTALL_URL ??
  process.env.SHOPIFY_APP_INSTALL_URL ??
  'https://apps.shopify.com/yespersonalized'

export const SITE_TAGLINE =
  'Live personalisation for Shopify print-on-demand'

export const SITE_DESCRIPTION =
  'YES! Personalized adds a live personalisation widget to any Shopify product. Build templates in a visual editor, let customers preview in real time, and get print-ready 300 DPI artwork on every order.'

/** Absolute URL for a site-relative path. */
export function absoluteUrl(path = '/'): string {
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * Metadata block for session-bound or app-internal routes. robots.txt already
 * disallows these paths, but a meta tag also covers URLs a crawler reaches via
 * an external link, which Disallow alone does not de-index.
 */
export const NOINDEX = {
  robots: { index: false, follow: false, nocache: true },
} as const

export const SOCIAL_IMAGE = {
  url: absoluteUrl('/opengraph-image.png'),
  width: 1200,
  height: 630,
  alt: `${SITE_NAME} — ${SITE_TAGLINE}`,
}

/**
 * Complete `metadata` block for an indexable marketing page.
 *
 * Next.js does NOT deep-merge `openGraph` / `twitter` — declaring either in a
 * route segment replaces the root layout's object wholesale, silently dropping
 * the share image, site name, locale and card type. So every page builds its
 * social blocks through here rather than hand-rolling a partial object.
 */
export function pageMetadata({
  path,
  title,
  description,
  ogType = 'website',
}: {
  path: string
  title: string
  description: string
  ogType?: 'website' | 'article'
}) {
  const fullTitle = `${title} — ${SITE_NAME}`

  return {
    title,
    description,
    alternates: { canonical: absoluteUrl(path) },
    openGraph: {
      type: ogType,
      siteName: SITE_NAME,
      locale: 'en_GB',
      url: absoluteUrl(path),
      title: fullTitle,
      description,
      images: [SOCIAL_IMAGE],
    },
    twitter: {
      card: 'summary_large_image' as const,
      title: fullTitle,
      description,
      images: [SOCIAL_IMAGE.url],
    },
  }
}

/** Pricing tiers, mirrored from the homepage pricing grid for JSON-LD offers. */
export const PLAN_OFFERS = [
  { name: 'Bronze', price: '19.99' },
  { name: 'Silver', price: '29.99' },
  { name: 'Gold', price: '49.99' },
  { name: 'Platinum', price: '99.99' },
] as const
